import { useCallback } from 'react';
import styled from 'styled-components';
import { discardPendingSongListUpdates, retrySongListSync } from '../../../data/use_current_org.tsx';
import { useSongListSaveRefusal } from '../../../data/use_song_list_save_refusal.ts';
import { confirm } from '../../../util/confirm.tsx';
import { attentionBannerBackground, attentionBannerDarkBackground } from '../../banner_colors.ts';

/**
 * Tells the admin that the server refuses the pending song list changes (issue #3609), while it does.
 *
 * It reflects the sync's refusal state and nothing else: it never takes focus, never opens anything by
 * itself, and owns its state so that a change in it re-renders only the banner, never the planner grid
 * (whose cells focus themselves when they re-render with a selection).
 *
 * It deliberately does not show the server's reason: that text is written for engineers (validation paths,
 * driver messages), and the server already records it with the payload as a song-lists.rejected audit row.
 * It stays one short row so the admin can keep planning in case the refusal is a passing problem.
 */
export function PlannerSaveRefusalBanner() {
  const refusal = useSongListSaveRefusal();

  const onRetry = useCallback(() => {
    retrySongListSync().catch((error: unknown) => {
      console.error(`Failed to retry the song list sync: ${(error as Error)?.message}`);
    });
  }, []);

  const onDiscard = useCallback(async () => {
    const confirmed = await confirm({
      confirmation: (
        <span>
          Discard the planner changes that couldn't be saved?
          <br />
          The planner will show what was last saved.
        </span>
      ),
      confirmButtonText: 'Discard',
      cancelButtonText: 'Cancel',
    });
    if (!confirmed) {
      return;
    }
    try {
      await discardPendingSongListUpdates();
    } catch (error: unknown) {
      // the pending changes are gone either way; the next sync downloads the saved lists
      console.error(`Failed to download song lists after discarding changes: ${(error as Error)?.message}`);
    }
  }, []);

  if (!refusal) {
    return null;
  }

  return <SongListSaveRefusalBanner onRetry={onRetry} onDiscard={() => void onDiscard()} />;
}

export function SongListSaveRefusalBanner({ onRetry, onDiscard }: { onRetry: () => void; onDiscard: () => void }) {
  return (
    <BannerContainer role="status" data-testid="song-list-save-refusal-banner">
      <Headline>Some planner changes couldn't be saved.</Headline>
      <ButtonRow>
        <PrimaryButton type="button" onClick={onRetry}>
          Try again
        </PrimaryButton>
        <SecondaryButton type="button" onClick={onDiscard}>
          Discard changes
        </SecondaryButton>
      </ButtonRow>
    </BannerContainer>
  );
}

const BannerContainer = styled.div`
  flex: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px 12px;
  padding: 6px 16px;
  background-color: ${attentionBannerBackground};
  color: var(--color-text-black);
  white-space: normal;

  [data-theme='dark'] & {
    background-color: ${attentionBannerDarkBackground};
    color: var(--color-text);
  }
`;

const Headline = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 0.9rem;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
`;

// Learn's PrimaryButton/SecondaryButton (learn_buttons.tsx) are full-width 52 px exercise buttons, and the
// subscription banner's are private and borderless when filled, so this inline pair keeps its own compact look.
const bannerButtonStyles = `
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 0.85rem;
  min-height: 30px;
  padding: 2px 12px;
  border-radius: 30px;
  border: 2px solid var(--color-primary-button);
  cursor: pointer;
  text-wrap: nowrap;
`;

const PrimaryButton = styled.button`
  ${bannerButtonStyles}
  color: white;
  background-color: var(--color-primary-button);
`;

const SecondaryButton = styled.button`
  ${bannerButtonStyles}
  color: var(--color-primary-button);
  background-color: white;
`;
